import { resolveNativeAppHandoff } from "./app-install-host.ts";
import { detectStorePlatform } from "./invite-link.ts";

export interface NativeAppHandoffContext {
  readonly hostname: string;
  readonly iosAppStoreUrl: string;
  readonly androidPlayStoreUrl: string;
  /** A Play Install Referrer value to append to the Play Store link — see
   *  `withAndroidReferrer`. Empty string omits the parameter entirely. The
   *  current caller (main.ts) always passes "": nothing in the app reads this
   *  yet (post-pilot, investigation #3), so it must never be fed the live
   *  invite claim token — that would leak credential material to Google. Wire
   *  a purpose-bound, one-time handoff value once the consumer lands. */
  readonly androidReferrerToken: string;
  readonly userAgent: string;
  readonly maxTouchPoints: number;
  readonly escapeHtml: (value: string) => string;
}

const ANDROID_REFERRER_PARAM = "referrer";

/**
 * Append `referrer=invite%3D{token}` to a Play Store URL (spec 5.4's state-
 * retention list, item 2). Nothing reads this yet — the Play Install Referrer
 * package is not in the app (investigation #3, shipped as a post-pilot
 * fallback) — so the link is inert today and ready the day that lands, with no
 * second edit to this page.
 */
function withAndroidReferrer(androidPlayStoreUrl: string, token: string): string {
  if (token === "") return androidPlayStoreUrl;
  const separator = androidPlayStoreUrl.includes("?") ? "&" : "?";
  const referrerValue = encodeURIComponent(`invite=${token}`);
  return `${androidPlayStoreUrl}${separator}${ANDROID_REFERRER_PARAM}=${referrerValue}`;
}

/** Render the production store badges, failing closed on every other host. */
export function appHandoffMarkup(context: NativeAppHandoffContext): string {
  const handoff = resolveNativeAppHandoff(
    context.hostname,
    context.iosAppStoreUrl,
    context.androidPlayStoreUrl,
  );
  if (handoff.kind === "internal-dev-client") return "";

  const iosStoreUrl = context.escapeHtml(handoff.iosAppStoreUrl);
  const androidStoreUrl = context.escapeHtml(
    withAndroidReferrer(handoff.androidPlayStoreUrl, context.androidReferrerToken),
  );
  const iosBadge =
    `<a class="store-badge" href="${iosStoreUrl}">` + "Download on the App Store</a>";
  const androidBadge =
    `<a class="store-badge" href="${androidStoreUrl}">` + "Get it on Google Play</a>";
  const platform = detectStorePlatform(context.userAgent, context.maxTouchPoints);
  const badges =
    platform === "ios"
      ? `${iosBadge}<div class="store-secondary">${androidBadge}</div>`
      : platform === "android"
        ? `${androidBadge}<div class="store-secondary">${iosBadge}</div>`
        : `${iosBadge}${androidBadge}`;
  return `<div class="stores">${badges}</div>`;
}
